import { buildResult, skip } from "../buildResult";

/** Mobile ingest (RunWhisperFunction is legacy). */
export const MOBILE_AUDIO_INGEST_LAMBDAS = [
  "MobileAudioProcessorFunction",
  "RunWhisperFunction",
];

/** Lambdas that indicate audio was ingested / transcribed (mobile or web). */
export const AUDIO_INGEST_LAMBDAS = [
  ...MOBILE_AUDIO_INGEST_LAMBDAS,
  "UploadConversationFunction",
];

export const CHATGPT_LAMBDA = "RunChatgptFunction";

export const DIARIZATION_LAMBDA = "RunDiarizationPipeline";

export const SUMMARIZATION_SLA_MS = 3 * 60 * 1000;

/**
 * Normalize /trace/fetch response into a flat list of trace events.
 * @param {*} trace
 * @returns {Array<{ lambda_name?: string, status?: string, patient_id?: string|number, uuid?: string, timestamp?: string, meta?: object }>}
 */
export function normalizeTraceEvents(trace) {
  if (!trace) return [];
  if (trace.error) return [];
  if (Array.isArray(trace)) return trace;
  if (Array.isArray(trace.events)) return trace.events;
  if (Array.isArray(trace.data)) return trace.data;
  if (Array.isArray(trace.records)) return trace.records;
  return [];
}

/**
 * @param {{ trace?: *, fetchErrors?: { trace?: string|null } }} ctx
 * @returns {{ ok: true, events: ReturnType<typeof normalizeTraceEvents> } | { ok: false, result: import('../types').ValidationTestResult }}
 */
export function resolveTrace(ctx) {
  const { trace, fetchErrors } = ctx;

  if (fetchErrors?.trace) {
    return { ok: false, result: skip(`Trace unavailable: ${fetchErrors.trace}`) };
  }

  if (trace?.error) {
    return {
      ok: false,
      result: buildResult({
        errors: [`Trace API error: ${trace.error}`],
        developerNotes: "POST /trace/fetch returned an error payload.",
      }),
    };
  }

  return { ok: true, events: normalizeTraceEvents(trace) };
}

/**
 * @param {ReturnType<typeof normalizeTraceEvents>} events
 * @param {string} lambdaName
 */
export function getLambdaEvents(events, lambdaName) {
  return events.filter((e) => e?.lambda_name === lambdaName);
}

/**
 * @param {ReturnType<typeof normalizeTraceEvents>} events
 * @param {string} lambdaName
 * @param {'START'|'END'} status
 */
export function hasLambdaStatus(events, lambdaName, status) {
  return events.some((e) => e?.lambda_name === lambdaName && e?.status === status);
}

/** @param {ReturnType<typeof normalizeTraceEvents>} events */
export function getUniqueLambdaNames(events) {
  return [...new Set(events.map((e) => e?.lambda_name).filter(Boolean))];
}

/**
 * Parse trace event timestamp (epoch ms string, e.g. "1777657954122").
 * @param {{ timestamp?: string|number }} event
 * @returns {number|null}
 */
export function parseEventTimestampMs(event) {
  const raw = event?.timestamp;
  if (raw == null || raw === "") return null;
  const ms = Number(raw);
  return Number.isFinite(ms) ? ms : null;
}

/** @param {number} ms */
export function formatDurationMs(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "unknown";
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min > 0) return `${min}m ${sec}s`;
  return `${sec}s`;
}

/** @param {ReturnType<typeof normalizeTraceEvents>} events */
export function getEndedIngestLambdas(events) {
  return AUDIO_INGEST_LAMBDAS.filter((name) => hasLambdaStatus(events, name, "END"));
}

/**
 * Longest RunChatgptFunction run: for each END, pair with the latest START at or
 * before that END, then take the maximum duration.
 *
 * @param {ReturnType<typeof normalizeTraceEvents>} events
 * @returns {{ startMs: number, endMs: number, durationMs: number } | null}
 */
export function findRunChatgptMaxDurationMs(events) {
  const starts = events
    .filter((e) => e?.lambda_name === CHATGPT_LAMBDA && e?.status === "START")
    .map((e) => parseEventTimestampMs(e))
    .filter((ms) => ms != null);

  const ends = events
    .filter((e) => e?.lambda_name === CHATGPT_LAMBDA && e?.status === "END")
    .map((e) => parseEventTimestampMs(e))
    .filter((ms) => ms != null);

  if (starts.length === 0 || ends.length === 0) return null;

  let best = null;

  for (const endMs of ends) {
    const eligibleStarts = starts.filter((startMs) => startMs <= endMs);
    if (eligibleStarts.length === 0) continue;

    const startMs = Math.max(...eligibleStarts);
    const durationMs = endMs - startMs;

    if (!best || durationMs > best.durationMs) {
      best = { startMs, endMs, durationMs };
    }
  }

  return best;
}

/**
 * Mobile if trace includes a mobile ingest lambda; otherwise WebApp.
 * @param {*} trace
 * @param {{ isLoading?: boolean }} [options]
 * @returns {{ platform: 'Mobile' | 'WebApp' | null, isLoading: boolean }}
 */
export function derivePlatformFromTrace(trace, { isLoading = false } = {}) {
  if (isLoading) {
    return { platform: null, isLoading: true };
  }
  const events = normalizeTraceEvents(trace);
  const isMobile = events.some((e) =>
    MOBILE_AUDIO_INGEST_LAMBDAS.includes(e?.lambda_name)
  );
  return { platform: isMobile ? "Mobile" : "WebApp", isLoading: false };
}
